import { useCallback, useEffect, useRef, useState } from "react";

const KEY = (clientId, tourId) => `tour:v1:${clientId || "anon"}:${tourId}`;

export function isTourDone(clientId, tourId) {
  try { return localStorage.getItem(KEY(clientId, tourId)) === "1"; } catch { return false; }
}
export function markTourDone(clientId, tourId) {
  try { localStorage.setItem(KEY(clientId, tourId), "1"); } catch { /* sin storage: se repite, no pasa nada */ }
}
export function resetTours(clientId) {
  try {
    Object.keys(localStorage).filter((k) => k.startsWith(`tour:v1:${clientId || "anon"}:`)).forEach((k) => localStorage.removeItem(k));
  } catch { /* noop */ }
}

const findTarget = (sel) => (sel ? document.querySelector(`[data-tour="${sel}"]`) : null);

/** Espera a que aparezca el elemento (la pestaña puede estar montando). */
function waitForTarget(sel, timeout = 1500) {
  return new Promise((resolve) => {
    if (!sel) return resolve(null);
    const start = performance.now();
    const tick = () => {
      const el = findTarget(sel);
      if (el) return resolve(el);
      if (performance.now() - start > timeout) return resolve(null);
      requestAnimationFrame(tick);
    };
    tick();
  });
}

/**
 * Estado del recorrido. `tours` es { id: [step] }; cada step: { target, tab, title,
 * body, optional }. Si un step pide otra pestaña, la cambia y espera al elemento; si
 * el elemento no existe y el step es `optional`, lo salta en la dirección del avance.
 */
export function useTour({ tours, clientId, activeTab, setActiveTab }) {
  const [run, setRun] = useState(null); // { id, steps, index, target }
  const dirRef = useRef(1);
  const seq = useRef(0);

  const resolve = useCallback(async (id, steps, index, dir, skipped = new Set()) => {
    const my = ++seq.current;
    let i = index;
    while (i >= 0 && i < steps.length) {
      const step = steps[i];
      const switching = !!step.tab && step.tab !== activeTab;
      if (switching) setActiveTab(step.tab);
      // Un opcional que no está en el DOM no debe hacer esperar: 300 ms y se salta.
      // eslint-disable-next-line no-await-in-loop
      const el = await waitForTarget(step.target, switching ? 2500 : step.optional ? 300 : 1200);
      if (my !== seq.current) return; // el usuario avanzó/cerró mientras esperábamos
      if (!el && step.optional) { skipped.add(i); i += dir; continue; }
      setRun({ id, steps, index: i, target: el, skipped });
      return;
    }
    // Se acabaron los pasos hacia adelante (o atrás): terminar.
    if (i >= steps.length) { markTourDone(clientId, id); setRun(null); }
  }, [activeTab, setActiveTab, clientId]);

  const start = useCallback((id) => {
    let steps = tours[id];
    if (!steps?.length) return;
    // Los opcionales de la pestaña actual (o sin pestaña) se resuelven YA contra el
    // DOM: así el contador "n de N" es exacto y no hay esperas al saltarlos.
    steps = steps.filter((st) => !(st.optional && (!st.tab || st.tab === activeTab) && !findTarget(st.target)));
    if (!steps.length) { markTourDone(clientId, id); return; }
    dirRef.current = 1;
    resolve(id, steps, 0, 1);
  }, [tours, resolve, activeTab, clientId]);

  const next = useCallback(() => {
    if (!run) return;
    if (run.index >= run.steps.length - 1) { markTourDone(clientId, run.id); setRun(null); return; }
    dirRef.current = 1;
    resolve(run.id, run.steps, run.index + 1, 1, run.skipped);
  }, [run, resolve, clientId]);

  const prev = useCallback(() => {
    if (!run || run.index === 0) return;
    dirRef.current = -1;
    resolve(run.id, run.steps, run.index - 1, -1, run.skipped);
  }, [run, resolve]);

  const close = useCallback(() => {
    if (run) markTourDone(clientId, run.id);
    seq.current++;
    setRun(null);
  }, [run, clientId]);

  // "No mostrar más": marca hechos TODOS los recorridos (bienvenida y cada pestaña) de
  // una vez. Se pueden repetir después con el botón de ayuda.
  const skipAll = useCallback(() => {
    Object.keys(tours).forEach((id) => markTourDone(clientId, id));
    seq.current++;
    setRun(null);
  }, [tours, clientId]);

  // Si el layout cambia (p. ej. la pestaña terminó de cargar datos), re-buscar el elemento.
  useEffect(() => {
    if (!run) return;
    const t = setTimeout(() => {
      const el = findTarget(run.steps[run.index]?.target);
      if (el && el !== run.target) setRun((r) => (r ? { ...r, target: el } : r));
    }, 600);
    return () => clearTimeout(t);
  }, [run]);

  // Numeración visible: solo cuenta los pasos que sí se muestran.
  const shown = run
    ? {
        index: run.index - [...run.skipped].filter((k) => k < run.index).length,
        total: run.steps.length - run.skipped.size,
      }
    : null;

  return { run, shown, start, next, prev, close, skipAll };
}
